export type LifecycleState =
  | "PROSPECT_NO_APP"
  | "PROSPECT_PARTIAL"
  | "PROSPECT_COMPLETE"
  | "ACTIVE_NOT_DISCLOSED"
  | "ACTIVE_DISCLOSED"
  | "POST_INTERIM_NO_PAYMENT"
  | "POST_INTERIM_PAYMENT_DUE"
  | "POST_AFN_SERVICING"
  | "POST_TRANSFERRED_FINAL"
  | "POST_TRANSFERRED_COMPLETE";

export type RespaFields = {
  borrowerName?: string;
  borrowerIncome?: number;
  borrowerSSN?: string;
  propertyAddress?: string;
  estimatedPropertyValue?: number;
  loanAmount?: number;
};

export type LoanFlags = {
  applicationStarted: boolean;
  applicationComplete: boolean;
  disclosuresIssued: boolean;
  loanClosed: boolean;
  firstPaymentDue: boolean;
  afnServicing?: boolean;
  servicingTransferred: boolean;
  finalPaymentMade: boolean;
};

export type LoanData = {
  respa: RespaFields;
  flags: LoanFlags;
};

export const respaCount = (r: RespaFields) =>
  [r.borrowerName, r.borrowerIncome, r.borrowerSSN, r.propertyAddress, r.estimatedPropertyValue, r.loanAmount]
    .filter((v) => v !== undefined && v !== null && v !== "").length;

export const meetsRespa = (r: RespaFields) => respaCount(r) === 6;

export function computeLifecycle(loan: LoanData): LifecycleState {
  const { respa, flags } = loan;
  if (flags.loanClosed) {
    if (flags.servicingTransferred) {
      return flags.finalPaymentMade ? "POST_TRANSFERRED_COMPLETE" : "POST_TRANSFERRED_FINAL";
    }
    if (flags.firstPaymentDue && !flags.afnServicing) {
      return "POST_INTERIM_PAYMENT_DUE";
    }
    if (flags.afnServicing) {
      return "POST_AFN_SERVICING";
    }
    return "POST_INTERIM_NO_PAYMENT";
  }
  // Once application is complete/submitted, promote to active state regardless of RESPA
  if (flags.applicationComplete) {
    return flags.disclosuresIssued ? "ACTIVE_DISCLOSED" : "ACTIVE_NOT_DISCLOSED";
  }
  if (!flags.applicationStarted) return "PROSPECT_NO_APP";
  return "PROSPECT_PARTIAL";
}

/**
 * NP-72: classify a Vesta CurrentLoanStage (the loan-list/loan-read
 * `currentStage` display-cache) into the app's lifecycle stage. The stage
 * names come from the AFN Vesta workspace's current loan-stage templates
 * (GET /api/loan-stage-templates, verified 2026-07-24):
 *   Prospects | File Start | Setup/Processing | Submitted to UW | Suspended |
 *   Conditional Approval | Resubmitted | Clear to close | Closing | Funded |
 *   Shipping | Post closing | Purchased | Canceled | Denied review | Denied
 * Matching is case-insensitive. Unknown/blank stages return 'unknown' so the
 * status-based derivation stays authoritative (fail-safe: no promotion).
 */
export type VestaStageClass = 'prospect' | 'active' | 'postClose' | 'terminal' | 'unknown';

const ACTIVE_STAGES = new Set([
  'file start', 'setup/processing', 'submitted to uw', 'suspended',
  'conditional approval', 'resubmitted', 'clear to close', 'closing',
]);
const POST_CLOSE_STAGES = new Set(['funded', 'shipping', 'post closing', 'purchased']);
const TERMINAL_STAGES = new Set(['canceled', 'denied review', 'denied']);

export function classifyVestaStage(stage: string | null | undefined): VestaStageClass {
  const s = (stage ?? '').trim().toLowerCase();
  if (!s) return 'unknown';
  if (s === 'prospects') return 'prospect';
  if (ACTIVE_STAGES.has(s)) return 'active';
  if (POST_CLOSE_STAGES.has(s)) return 'postClose';
  if (TERMINAL_STAGES.has(s)) return 'terminal';
  return 'unknown';
}

export type TabKey = "dashboard" | "application" | "myloan" | "myhome" | "documents" | "conditions" | "payments" | "income" | "contacts" | "tasks";

export const visibleTabs = (state: LifecycleState): TabKey[] => {
  switch (state) {
    // NP-499: No-application borrowers should not see the Application tab.
    // Tasks and Documents both need a real loan (loanGuid) behind them, which
    // doesn't exist until Personal Info is submitted, i.e. once
    // applicationStarted flips true and the state advances past PROSPECT_NO_APP.
    case "PROSPECT_NO_APP": return ["dashboard"];
    case "PROSPECT_PARTIAL": return ["dashboard", "application", "tasks", "documents", "income"];
    case "PROSPECT_COMPLETE": return ["dashboard", "application", "myloan", "tasks", "documents", "income"];
    // NP-72: My Home appears in ALL of State 3 (Active Loan) — the ticket does
    // not condition it on disclosures, and no API field reports disclosure
    // status anyway.
    case "ACTIVE_NOT_DISCLOSED": return ["dashboard", "myloan", "myhome", "tasks", "documents", "conditions", "income", "contacts"];
    case "ACTIVE_DISCLOSED": return ["dashboard", "myloan", "myhome", "tasks", "documents", "conditions", "income", "contacts"];
    case "POST_INTERIM_NO_PAYMENT":
    case "POST_INTERIM_PAYMENT_DUE":
    case "POST_AFN_SERVICING":
    case "POST_TRANSFERRED_FINAL":
      return ["dashboard", "myloan", "myhome", "payments", "tasks", "documents", "contacts"];
    case "POST_TRANSFERRED_COMPLETE":
      return ["dashboard", "myhome", "documents", "contacts"];
  }
};

export const stageOf = (s: LifecycleState | null | undefined): "Prospect" | "Active Loan" | "Post-Close" => {
  // Task 3 fix (2026-06-19): Guard against null/undefined — can occur during initial
  // render race on hard refresh before LoanContext settles. Returning 'Prospect' is the
  // safe default (matches PROSPECT_NO_APP behaviour) and prevents SophiaChat throws.
  if (!s) return 'Prospect';
  return s.startsWith('PROSPECT') ? 'Prospect' : s.startsWith('ACTIVE') ? 'Active Loan' : 'Post-Close';
};

export const stateLabel = (s: LifecycleState): string => ({
  PROSPECT_NO_APP: "No Application",
  PROSPECT_PARTIAL: "Partial Application",
  PROSPECT_COMPLETE: "Application Complete",
  ACTIVE_NOT_DISCLOSED: "Pending Disclosures",
  ACTIVE_DISCLOSED: "In Processing",
  POST_INTERIM_NO_PAYMENT: "Just Closed",
  POST_INTERIM_PAYMENT_DUE: "First Payment Due",
  POST_AFN_SERVICING: "AFN Servicing",
  POST_TRANSFERRED_FINAL: "Transferring Servicing",
  POST_TRANSFERRED_COMPLETE: "Servicing Transferred",
}[s]);

export const sophiaPersona = (s: LifecycleState) => {
  switch (s) {
    case "PROSPECT_NO_APP":
      return { tone: "Onboarding guide", greeting: "Welcome! Ready to start your home journey?" };
    case "PROSPECT_PARTIAL":
      return { tone: "Progress cheerleader", greeting: "You're making great progress — let's keep going." };
    case "PROSPECT_COMPLETE":
      return { tone: "Home shopping assistant", greeting: "Your application is complete! Let's find your home." };
    case "ACTIVE_NOT_DISCLOSED":
      return { tone: "Professional processor", greeting: "Your file is in review. I'll keep you posted." };
    case "ACTIVE_DISCLOSED":
      return { tone: "Proactive milestone announcer", greeting: "Disclosures are out — here's what's next." };
    case "POST_INTERIM_NO_PAYMENT":
    case "POST_INTERIM_PAYMENT_DUE":
      return { tone: "Servicing educator", greeting: "Congrats on closing! Let's set up servicing." };
    case "POST_AFN_SERVICING":
      return { tone: "Wealth coach", greeting: "Your loan is live — let's grow your equity." };
    case "POST_TRANSFERRED_FINAL":
    case "POST_TRANSFERRED_COMPLETE":
      return { tone: "Transition advisor", greeting: "Servicing is moving — I'll guide the handoff." };
  }
};
